/**
 * Mini Recorder Component
 *
 * A persistent floating recorder that allows users to continue recording
 * while navigating the app. Renders at the app root level outside routes.
 */

import React, { useEffect, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Draggable from 'react-draggable'
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  LinearProgress,
  Stack,
  Tooltip,
  Collapse
} from '@mui/material'
import {
  Mic as MicIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  OpenInFull as ExpandIcon,
  FiberManualRecord as RecordIcon
} from '@mui/icons-material'
import { toast } from 'react-toastify'

import { RootState } from '../../redux/store'
import {
  pauseRecording,
  resumeRecording,
  stopRecording,
  updateDuration,
  setUploading,
  setJobInfo,
  updateJobStatus,
  setError,
  toggleMinimized,
  resetRecording
} from '../../redux/recording-slice'
import { useAudioRecorder, formatDuration, getSupportedMimeType } from '../../hooks/use-audio-recorder'
import { scribeService } from '../../services/scribe.service'
import {
  createDraft,
  appendChunk,
  deleteDraft,
  updateDraftDuration,
} from '../../utils/recordingPersistence'

const MiniRecorderComponent: React.FC = () => {
  const dispatch = useDispatch()
  const nodeRef = useRef<HTMLDivElement>(null)
  const cancelPollRef = useRef<(() => void) | null>(null)
  // Id of the durable IndexedDB draft for the active recording (CAPA F131). Each
  // audio chunk is persisted to it as it arrives, so a session-timeout logout / reload
  // / crash leaves a recoverable draft instead of losing the whole encounter.
  const draftIdRef = useRef<string | null>(null)

  const {
    isRecording,
    isPaused,
    isUploading,
    isProcessing,
    patientId,
    patientRefId,
    patientName,
    callType,
    gender,
    duration,
    jobStatus,
    jobProgress,
    error,
    miniRecorderVisible,
    miniRecorderMinimized
  } = useSelector((state: RootState) => state.recording)

  // Audio recorder hook
  const {
    status: recorderStatus,
    duration: recorderDuration,
    error: recorderError,
    startRecording: startAudio,
    pauseRecording: pauseAudio,
    resumeRecording: resumeAudio,
    stopRecording: stopAudio,
    resetRecording: resetAudio
  } = useAudioRecorder({
    onRecordingComplete: handleRecordingComplete,
    // Persist each chunk to the active draft as it arrives (CAPA F131). Best-effort:
    // if no draft was created (missing context / IndexedDB unavailable), this no-ops.
    onChunk: (chunk: Blob) => {
      if (draftIdRef.current) {
        appendChunk(draftIdRef.current, chunk).catch(() => { /* best-effort */ })
      }
    }
  })

  // Start actual recording when mini-recorder becomes visible with isRecording=true.
  // If recorderStatus is 'stopped' (left over from a previous completed session),
  // call resetAudio() first — that sets status back to 'idle' and duration to 0,
  // which re-triggers this effect and then calls startAudio().
  useEffect(() => {
    if (miniRecorderVisible && isRecording) {
      if (recorderStatus === 'idle') {
        let cancelled = false
        // Create the durable draft BEFORE starting capture so the first chunk
        // (≈1s in) always has a draft to attach to (CAPA F131). Persistence is
        // best-effort — recording starts regardless of whether the draft was created.
        ;(async () => {
          if (!draftIdRef.current && patientId && callType) {
            try {
              draftIdRef.current = await createDraft({
                patientId,
                patientRefId: patientRefId || patientId,
                patientName: patientName || `Patient ${patientId}`,
                callType,
                gender,
                mimeType: getSupportedMimeType(),
              })
            } catch (_) {
              /* draft creation failed — recording still proceeds without recovery */
            }
          }
          if (!cancelled) startAudio()
        })()
        return () => { cancelled = true }
      } else if (recorderStatus === 'stopped') {
        resetAudio()
      }
    }
  }, [
    miniRecorderVisible, isRecording, recorderStatus, startAudio, resetAudio,
    patientId, patientRefId, patientName, callType, gender,
  ])

  // Sync duration from recorder to Redux
  useEffect(() => {
    if (isRecording && !isPaused) {
      dispatch(updateDuration(recorderDuration))
      // Stamp the running duration onto the draft every ~10s so a recovered draft can
      // show roughly how long the lost recording was (CAPA F131). Throttled to limit
      // IndexedDB writes; best-effort.
      if (draftIdRef.current && recorderDuration > 0 && recorderDuration % 10 === 0) {
        updateDraftDuration(draftIdRef.current, recorderDuration).catch(() => { /* best-effort */ })
      }
    }
  }, [recorderDuration, isRecording, isPaused, dispatch])

  // Sync errors from recorder to Redux
  useEffect(() => {
    if (recorderError) {
      dispatch(setError(recorderError))
    }
  }, [recorderError, dispatch])

  // Handle recording complete - upload to backend
  async function handleRecordingComplete(blob: Blob) {
    if (!patientId || !callType) {
      dispatch(setError('Missing patient or call type information'))
      return
    }

    dispatch(setUploading(true))

    try {
      const response = await scribeService.createJob({
        audioFile: blob,
        patientId: patientId,
        callType: callType,
        gender: gender || undefined
      })

      if (response.success && response.job_id) {
        dispatch(setJobInfo({ jobId: response.job_id, status: response.status }))

        // Upload succeeded — the server now holds the audio, so the durable draft is
        // no longer needed (CAPA F131). Delete it so it is not offered for recovery.
        if (draftIdRef.current) {
          deleteDraft(draftIdRef.current).catch(() => { /* best-effort */ })
          draftIdRef.current = null
        }

        // Start polling for job completion
        cancelPollRef.current = scribeService.pollJobStatus(
          response.job_id,
          (status) => {
            dispatch(updateJobStatus({ status: status.status, progress: status.progress_percent }))
          },
          () => {
            dispatch(updateJobStatus({ status: 'completed', progress: 100 }))
            toast.success(
              <span>
                Recording processed successfully!{' '}
                <a href={`/patients/${patientRefId}/details`} style={{ color: '#fff', textDecoration: 'underline' }}>
                  View Results
                </a>
              </span>,
              { autoClose: 10000 }
            )
          },
          (error) => {
            dispatch(setError(error.message))
            toast.error(`Processing failed: ${error.message}`)
          }
        )
      } else {
        dispatch(setError(response.error || 'Failed to create job'))
      }
    } catch (err: any) {
      dispatch(setError(err.message || 'Failed to upload recording'))
    }
  }

  // Handle pause button click
  const handlePause = useCallback(() => {
    pauseAudio()
    dispatch(pauseRecording())
  }, [pauseAudio, dispatch])

  // Handle resume button click
  const handleResume = useCallback(() => {
    resumeAudio()
    dispatch(resumeRecording())
  }, [resumeAudio, dispatch])

  // Handle stop button click
  const handleStop = useCallback(() => {
    stopAudio()
    dispatch(stopRecording())
  }, [stopAudio, dispatch])

  // Handle close button click
  const handleClose = useCallback(() => {
    // Cancel any ongoing polling
    if (cancelPollRef.current) {
      cancelPollRef.current()
      cancelPollRef.current = null
    }

    // Stop recording if in progress
    if (recorderStatus === 'recording' || recorderStatus === 'paused') {
      stopAudio()
    }

    resetAudio()
    dispatch(resetRecording())
  }, [recorderStatus, stopAudio, resetAudio, dispatch])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cancelPollRef.current) {
        cancelPollRef.current()
      }
    }
  }, [])

  // Don't render if not visible
  if (!miniRecorderVisible) {
    return null
  }

  // Get status text
  const getStatusText = () => {
    if (error) return 'Error'
    if (isUploading) return 'Uploading...'
    if (isProcessing) {
      switch (jobStatus) {
        case 'transcribing': return 'Transcribing...'
        case 'diarizing': return 'Identifying speakers...'
        case 'summarizing': return 'Generating summary...'
        default: return 'Processing...'
      }
    }
    if (isPaused) return 'Paused'
    if (isRecording) return 'Recording'
    return 'Ready'
  }

  // Get call type display name
  const callTypeDisplay: Record<string, string> = {
    CC: 'Cardiology Consultation',
    CF: 'Cardiology Follow-up',
    DI: 'Data Intake',
    ME: 'Medication Evaluation',
    MB: 'Medication Baseline',
    MI: 'Medication Initiation',
    WC: 'Welcome Call',
    OC: 'Onboarding Call',
    ET: 'Early Toxicity'
  }

  return (
    <Draggable handle=".mini-recorder-handle" nodeRef={nodeRef} bounds="parent">
      <Paper
        ref={nodeRef}
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1300, // Above most content, below modals
          minWidth: miniRecorderMinimized ? 180 : 320,
          maxWidth: 400,
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.2s ease'
        }}
      >
        {/* Header - always visible, draggable */}
        <Box
          className="mini-recorder-handle"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1,
            bgcolor: isRecording ? 'error.main' : isProcessing ? 'info.main' : 'primary.main',
            color: 'white',
            cursor: 'move'
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <MicIcon fontSize="small" />
            {miniRecorderMinimized ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                {isRecording && (
                  <RecordIcon
                    sx={{
                      fontSize: 12,
                      color: isPaused ? 'rgba(255,255,255,0.5)' : 'white',
                      animation: isPaused ? 'none' : 'pulse 1s infinite'
                    }}
                  />
                )}
                <Typography variant="body2" fontWeight="bold">
                  {formatDuration(duration)}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 180 }}>
                {patientName || 'Recording'}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <Tooltip title={miniRecorderMinimized ? 'Expand' : 'Minimize'}>
              <IconButton size="small" onClick={() => dispatch(toggleMinimized())} sx={{ color: 'white' }}>
                {miniRecorderMinimized ? <ExpandIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Content - collapsible */}
        <Collapse in={!miniRecorderMinimized}>
          <Box sx={{ p: 2 }}>
            {/* Patient & Call Type Info */}
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {callType && callTypeDisplay[callType]}
            </Typography>

            {/* Recording Timer & Status */}
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ my: 2 }}>
              {isRecording && (
                <RecordIcon
                  sx={{
                    fontSize: 16,
                    color: isPaused ? 'text.disabled' : 'error.main',
                    animation: isPaused ? 'none' : 'pulse 1s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.3 },
                      '100%': { opacity: 1 }
                    }
                  }}
                />
              )}
              <Typography variant="h4" fontWeight="bold" fontFamily="monospace">
                {formatDuration(duration)}
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary" textAlign="center" gutterBottom>
              {getStatusText()}
            </Typography>

            {/* Progress bar during processing */}
            {isProcessing && (
              <LinearProgress
                variant="determinate"
                value={jobProgress}
                sx={{ my: 1, borderRadius: 1 }}
              />
            )}

            {/* Error message */}
            {error && (
              <Typography variant="body2" color="error" textAlign="center" sx={{ my: 1 }}>
                {error}
              </Typography>
            )}

            {/* Controls */}
            {isRecording && !isUploading && !isProcessing && (
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                {isPaused ? (
                  <Button
                    variant="outlined"
                    startIcon={<PlayIcon />}
                    onClick={handleResume}
                  >
                    Resume
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<PauseIcon />}
                    onClick={handlePause}
                  >
                    Pause
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                >
                  Stop & Process
                </Button>
              </Stack>
            )}

            {/* Processing complete message */}
            {jobStatus === 'completed' && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Typography variant="body2" color="success.main" textAlign="center">
                  Processing complete!
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClose}
                  sx={{ alignSelf: 'center' }}
                >
                  Close
                </Button>
              </Stack>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Draggable>
  )
}

export default MiniRecorderComponent
